import { useState, useCallback } from "react";
import { Button, Flex, Text } from "@radix-ui/themes";
import { Input } from "@/components/ui/input";
import { Dialog } from "@/components/shared/dialog";
import { useSubTopicApi } from "@/v2/entities/subtopic/SubTopic.api";
import { Loader2Icon } from "lucide-react";
import { toast } from "sonner";

interface CreateSubTopicDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  topicId: number;
  onSuccess: () => void;
}

export function CreateSubTopicDialog({
  open,
  onOpenChange,
  topicId,
  onSuccess,
}: CreateSubTopicDialogProps) {
  const [name, setName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const subTopicApi = useSubTopicApi();

  const handleCreate = useCallback(async () => {
    if (!name.trim()) return;
    setIsLoading(true);
    try {
      await subTopicApi.createSubTopic({ name: name.trim(), topicId });
      toast.success("SubTopic created successfully");
      setName("");
      onOpenChange(false);
      onSuccess();
    } catch (err) {
      toast.error("Failed to create subtopic");
    } finally {
      setIsLoading(false);
    }
  }, [name, topicId, subTopicApi, onSuccess, onOpenChange]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) setName("");
      onOpenChange(next);
    },
    [onOpenChange]
  );

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content size="small">
        <Flex direction="column" gap="4" p="4">
          <Dialog.Title>Create SubTopic</Dialog.Title>
          <Flex direction="column" gap="2">
            <Text size="2" weight="medium">
              Name
            </Text>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="SubTopic name"
            />
          </Flex>
          <Flex gap="3" justify="end">
            <Button
              variant="soft"
              color="gray"
              onClick={() => handleOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!name.trim() || isLoading}
              highContrast
            >
              {isLoading ? <Loader2Icon className="animate-spin" /> : "Create"}
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
}
